import React from 'react';
import { useTranslation } from 'react-i18next';
import { StatusBadgeConfigType } from '@hero/web-components';
import { Fee, Order } from '@hero/schema';
import { OrderRefIcon, TruckIcon } from '@hero/web-icons';
import { OrderStatusBadge } from '../Status/OrderStatusBadge';
import { OrderRouteDisplay } from '../Route/OrderRouteDisplay';
import { OrderAgainManager } from '../OrderAgain';
import { Price } from '../../../app/Price';
import {
  OrderDisplayInfo,
  OrderDisplayPriceUi,
  OrderDisplayPricing,
  OrderDisplayStatus,
  OrderDisplayVisibility,
  UseOrderAgainConfig,
} from '@hero/hooks-shared';

/**
 * Auto-managed fees we hide from headline summing — kept in sync with
 * `PriceCell` so the load-price + fees display stays consistent across
 * table and drawer surfaces.
 */
const HIDDEN_FEE_TYPES = new Set(['items_insurance']);

export interface OrderSummaryCardProps {
  order: Order;
  orderInfo: OrderDisplayInfo;
  pricing: OrderDisplayPricing;
  status: OrderDisplayStatus;
  visibility: OrderDisplayVisibility;
  priceUi: OrderDisplayPriceUi;
  displayForUser: 'customer' | 'carrier';
  statusConfig?: StatusBadgeConfigType;
  orderAgainConfig?: UseOrderAgainConfig;
  /** Opens assign/change client when the carrier can manage the order. */
  onEditClient?: () => void;
}

export const OrderSummaryCard: React.FC<OrderSummaryCardProps> = ({
  order,
  orderInfo,
  pricing,
  status,
  visibility,
  priceUi,
  displayForUser,
  statusConfig,
  orderAgainConfig,
  onEditClient,
}) => {
  const { t } = useTranslation('m-order');

  const loadPrice =
    priceUi.amount?.itemsOriginalSubtotal ??
    priceUi.amount?.subtotal ??
    pricing.subtotal;
  const headlineCurrency = priceUi.currency ?? pricing.currency;

  const visibleFees: Fee[] = (priceUi.amount?.fees?.applied ?? []).filter(
    f => !HIDDEN_FEE_TYPES.has(f.type),
  );
  const feesTotal = visibleFees.reduce((acc, f) => acc + (f.amount || 0), 0);

  const carrierName = order.carrier?.name;

  return (
    <div className="p-5 border rounded-lg">
      <div className="flex items-start justify-between mb-4">
        {visibility.showPrice ? (
          <div className="flex flex-col gap-0.5">
            <div className="text-[20px] font-[500] leading-[28px] text-black">
              <Price amount={loadPrice} currency={headlineCurrency} />
            </div>
            {feesTotal > 0 && (
              <div className="flex items-center gap-1 text-[12px] leading-[16px] text-gray-500">
                <span>{t('table.price_cell.fees')}:</span>
                <Price amount={feesTotal} currency={headlineCurrency} size="xs" />
              </div>
            )}
          </div>
        ) : (
          <span />
        )}

        <OrderStatusBadge status={status.currentStatus} statusConfig={statusConfig} />
      </div>

      <div className="flex items-center justify-between pb-3 mb-4 border-b">
        <OrderRouteDisplay
          destinations={[orderInfo.pickup!, orderInfo.dropoff!]}
          vehicle={orderInfo.vehicleType}
          order={order}
          className="justify-between w-full"
          onClientClick={onEditClient}
          addClientLabel={
            onEditClient ? t('modal.change_client.add_client') : undefined
          }
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-gray-600 body2">
          <OrderRefIcon className="w-5 h-5 text-black" /> {orderInfo.ref.main}
        </div>
        {carrierName ? (
          <div className="flex items-center gap-2 text-gray-600 body2">
            <TruckIcon className="w-5 h-5 text-black" />
            <span className="truncate max-w-[180px]">{carrierName}</span>
          </div>
        ) : priceUi.isInternalShipment ? (
          <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 border border-amber-200">
            {t('badges.internal')}
          </span>
        ) : null}
      </div>

      {visibility.showOrderAgainButton && orderAgainConfig && (
        <div className="mt-3">
          <OrderAgainManager
            order={order}
            config={orderAgainConfig}
            displayForUser={displayForUser}
            showButton={visibility.showOrderAgainButton}
          />
        </div>
      )}
    </div>
  );
};
